
import { Box, TextField, MenuItem, FormControl, InputLabel, Select, Typography, SvgIcon, Chip, FormControlLabel, Checkbox, Switch, Fade, List, ListItem, ListItemIcon, ListItemText, Grid, Alert, Paper } from "@mui/material";


import NumbersIcon from '@mui/icons-material/Numbers';
import React, { useEffect } from "react";
import { NumericFormat, NumericFormatProps } from 'react-number-format';
import { PAYMENT_METHODS_ICONS, RAFFLE_TYPE_ICONS } from "../../../../constants/icons";
import { PAYMENT_METHODS } from "../../../../constants/paymentMethods";
import { CreateStepperRaffleProps } from "../../../../domain/CreateStepperRaffleProps";
import { ICreationRaffle } from "../../../../store/interfaces/ICreationRaffle";
import { BadgeCheck } from "lucide-react";
import { isTalonario, TALONARIO_MAX_LIMIT } from "../../../../utils/raffleType";


interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const NumericFormatCustom = React.forwardRef<NumericFormatProps, CustomProps>(
  function NumericFormatCustom(props, ref) {
    const { onChange, ...other } = props;

    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: Number.parseInt(values.value).toString(),
            },
          });
        }}
        thousandSeparator
        valueIsNumericString
        prefix="$"
      />
    );
  },
);

export const MainFeaturesStep = ({resource, setResource, setDisableNext, accountId}: CreateStepperRaffleProps) => {

  // Determinar si debe ser premium por defecto (todas excepto agudojhonny)
  const shouldBePremium = accountId !== 'agudojhonny';
  
  // Asegurar valores por defecto solo si no existen
  const safeResource: ICreationRaffle = {
    ...resource,
    isPremium: shouldBePremium || resource.isPremium || false,
    availablePaymentMethods: resource.availablePaymentMethods || [],
    name: resource.name || "",
    type: resource.type || "",
  };
  
  const isPremiumRaffle = safeResource.isPremium;
  
  // Asegurar que isPremium sea correcto al montar
  useEffect(() => {
    if (shouldBePremium && !safeResource.isPremium) {
      setResource({ ...safeResource, isPremium: true });
    }
  }, [shouldBePremium]); // Solo cuando cambia shouldBePremium
  
  const toggleIsPremiumRaffle = (check: boolean) => {
    // Solo permitir cambiar si es agudojhonny
    if (shouldBePremium) {
      return;
    }
    setResource({ ...safeResource, isPremium: check });
  };

  const premiumFeatures = [
    "Todas lo de una rifa basica",
    "Retraso de números premiados",
    "Soporte vía WhatsApp + Auto revisión para los usuarios",
    "Todos los reportes habilitados: compradores, hora de compra, países de compra",
    "Promociones: envío a los mejores compradores de antiguas rifas",
  ];
 
  const basicFeatures = [
    "Gestion de ordenes de compra",
    "Visualizacion de números disponibles",
    "Reportes de ingresos separados por cuenta",
    "Reenvio de tickets a los compradores",
  ];


  const handleChange = (event: any) => {
    const { name, value } = event.target;
    
    // Usar el resource actual directamente desde las props
    const updatedResource = { ...resource };
    
    if (name === "ticketPrice" || name === "ticketPriceUSD") {
      const newValue = Number.parseInt(value) || 0;
      // @ts-ignore
      updatedResource[name] = newValue;
    } else if (name === "availablePaymentMethods") {
      // @ts-ignore
      updatedResource[name] = Array.isArray(value) ? value : [];
    } else {
      // @ts-ignore
      updatedResource[name] = value;
    }

    if (name === "type" && isTalonario(value) && updatedResource.limit > TALONARIO_MAX_LIMIT) {
      updatedResource.limit = TALONARIO_MAX_LIMIT;
    }
    
    // Actualizar el resource del padre
    setResource(updatedResource);
  };

  useEffect(() => {
    if (setDisableNext !== undefined) {
      const isFormValid =
        safeResource.name.trim() !== "" &&
        safeResource.ticketPrice > 0 &&
        safeResource.limit > 0 &&
        safeResource.type !== "" &&
        safeResource.availablePaymentMethods.length > 0;
    
      setDisableNext(!isFormValid);
    }
  }, [safeResource.name, safeResource.ticketPrice, safeResource.limit, safeResource.type, safeResource.availablePaymentMethods, setDisableNext]);
  
  const handleChangeHasReferenceNumber = (event: any) => {
    setResource({ ...resource, hasReferenceNumber: event.target.checked });
  };

  return (
    <Box p={3}>
      <Grid container spacing={3}>
        
        {/* Información Básica */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
              Información Básica
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  required
                  label="Nombre de la Rifa"
                  variant="outlined"
                  fullWidth
                  name="name"
                  value={safeResource.name}
                  onChange={handleChange}
                  inputProps={{ maxLength: 48 }}
                  helperText="Máximo 48 caracteres"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>Tipo de concurso</InputLabel>
                  <Select
                    required
                    label="Tipo de concurso"
                    name="type"
                    value={safeResource.type}
                    onChange={handleChange}
                  >
                    {RAFFLE_TYPE_ICONS.map((element) => (
                      <MenuItem key={element.id} value={element.id}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {element.icon}
                          <Typography ml={1}>{element.name}</Typography>
                        </div>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl variant="outlined" fullWidth disabled={safeResource.type === ''}>
                  <InputLabel>Número de tickets</InputLabel>
                  <Select
                    required
                    label="Número de tickets"
                    name="limit"
                    value={safeResource.limit}
                    onChange={handleChange}
                    renderValue={(value) => {
                      return (
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                          <SvgIcon color="primary">
                            <NumbersIcon />
                          </SvgIcon>
                          <Typography>{`${value} tickets`}</Typography>
                        </Box>
                      );
                    }}
                  >
                    <MenuItem value={100}>100</MenuItem>
                    <MenuItem value={1000}>1000</MenuItem>
                    <MenuItem disabled={isTalonario(safeResource.type)} value={10000}>10000</MenuItem>
                    <MenuItem disabled={safeResource.type !== 'promo'} value={100000}>100000</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Precios */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
              Precios
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TextField
                    label="Precio del ticket"
                    variant="outlined"
                    required
                    fullWidth
                    name="ticketPrice"
                    value={safeResource.ticketPrice}
                    onChange={handleChange}
                    inputProps={{ maxLength: 7 }}
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                    }}
                    helperText="Precio en moneda local"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TextField
                    label="Precio del ticket en dólares"
                    variant="outlined"
                    required
                    fullWidth
                    name="ticketPriceUSD"
                    value={safeResource.ticketPriceUSD}
                    onChange={handleChange}
                    inputProps={{ maxLength: 7 }}
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                    }}
                    helperText="Precio en USD"
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Métodos de Pago */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
              Métodos de Pago
            </Typography>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Seleccionar métodos de pago</InputLabel>
              <Select
                required
                multiple
                name="availablePaymentMethods"
                label="Seleccionar métodos de pago"
                value={safeResource.availablePaymentMethods}
                onChange={handleChange}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip 
                        variant="outlined" 
                        key={value} 
                        label={value}  
                        icon={PAYMENT_METHODS_ICONS("20px").find((icon) => icon.id === value)?.icon} 
                        size="medium" 
                      />
                    ))}
                  </Box>
                )}
              >
                {PAYMENT_METHODS.map((element) => (
                  <MenuItem key={element.id} value={element.id}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {PAYMENT_METHODS_ICONS("20px").find((icon) => icon.id === element.id)?.icon}
                      <Typography ml={1}>{element.name}</Typography>
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>
        </Grid>

        {/* Configuración Adicional */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
              Configuración Adicional
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel 
                  control={
                    <Checkbox
                      checked={safeResource.hasReferenceNumber}
                      onChange={(event) => handleChangeHasReferenceNumber(event)}
                    />
                  } 
                  label="Validación número de referencia" 
                />
                {safeResource.hasReferenceNumber && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Las compras requerirán el número de referencia
                  </Alert>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Tipo de Rifa: Básica vs Premium */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
            {shouldBePremium ? (
              <Box display="flex" alignItems="center" justifyContent="center" gap={2} mb={3}>
                <Typography variant="h6" color="primary" fontWeight="600">
                  Rifa Premium
                </Typography>
                <Chip 
                  label="Activo" 
                  color="primary" 
                  variant="filled"
                  sx={{ fontWeight: "bold" }}
                />
              </Box>
            ) : (
              <Box display="flex" alignItems="center" justifyContent="center" gap={2} mb={3}>
                <Typography variant="h6" color={!isPremiumRaffle ? "primary" : "text.secondary"}>
                  Básica
                </Typography>
                <Switch
                  checked={isPremiumRaffle}
                  onChange={($event: any) => {
                    toggleIsPremiumRaffle($event.target.checked)
                  }}
                  inputProps={{ "aria-label": "controlled" }}
                  size="medium"
                />
                <Typography variant="h6" color={isPremiumRaffle ? "primary" : "text.secondary"}>
                  Premium
                </Typography>
              </Box>
            )}

            <Grid container spacing={3}>
              {/* Basic Features - Solo visible si no es premium forzado */}
              {!shouldBePremium && (
                <Grid item xs={12} md={6}>
                  <Fade in={!isPremiumRaffle} timeout={500}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: isPremiumRaffle ? '1px solid' : '2px solid',
                        borderColor: isPremiumRaffle ? 'divider' : 'primary.main',
                        opacity: isPremiumRaffle ? 0.4 : 1,
                        transition: "all 0.3s ease-in-out",
                        bgcolor: isPremiumRaffle ? 'grey.50' : 'background.paper',
                      }}
                    >
                      <Typography variant="h6" mb={1} fontWeight="600">
                        Rifa Básica
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        Precio de mantención: <strong>$50 USD</strong>
                      </Typography>
                      <List dense>
                        {basicFeatures.map((feature, index) => (
                          <ListItem key={index} disablePadding sx={{ mb: 1 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <BadgeCheck color={isPremiumRaffle ? "gray" : "blue"} />
                            </ListItemIcon>
                            <ListItemText
                              primary={feature}
                              primaryTypographyProps={{
                                fontSize: "0.95rem",
                                color: isPremiumRaffle ? "text.disabled" : "text.primary",
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Fade>
                </Grid>
              )}

            </Grid>
          </Paper>
        </Grid>

      </Grid>
    </Box>
  );
};
